import { jsx } from '@emotion/react/jsx-runtime';
import React, { useRef, useMemo, useEffect, useContext } from 'react';

var BitsComponents;
(function (BitsComponents) {
  BitsComponents["Button"] = "Button";
  BitsComponents["SegmentedContainer"] = "SegmentedContainer";
  BitsComponents["SegmentedButton"] = "SegmentedButton";
  BitsComponents["Tabs"] = "Tabs";
  BitsComponents["ColorPicker"] = "ColorPicker";
  BitsComponents["Link"] = "Link";
  BitsComponents["Alert"] = "Alert";
  BitsComponents["TextInput"] = "TextInput";
  BitsComponents["Typography"] = "Typography";
  BitsComponents["Avatar"] = "Avatar";
  BitsComponents["Tooltip"] = "Tooltip";
  BitsComponents["Menu"] = "Menu";
  BitsComponents["Checkbox"] = "Checkbox";
  BitsComponents["MenuItem"] = "MenuItem";
  BitsComponents["MenuToggleItem"] = "MenuToggleItem";
  BitsComponents["MenuSwitchItem"] = "MenuSwitchItem";
  BitsComponents["MenuDividerItem"] = "MenuDividerItem";
  BitsComponents["MenuCheckItem"] = "MenuCheckItem";
  BitsComponents["MenuNestedItem"] = "MenuNestedItem";
  BitsComponents["Loader"] = "Loader";
  BitsComponents["Dropdown"] = "Dropdown";
  BitsComponents["MenuSectionTitle"] = "MenuSectionTitle";
  BitsComponents["Switch"] = "Switch";
  BitsComponents["Breadcrumbs"] = "Breadcrumbs";
})(BitsComponents || (BitsComponents = {}));
var MuiComponents;
(function (MuiComponents) {
  MuiComponents["button"] = "button";
})(MuiComponents || (MuiComponents = {}));

var InteractionsTypes;
(function (InteractionsTypes) {
  InteractionsTypes["Click"] = "Click";
  InteractionsTypes["Show"] = "Show";
  InteractionsTypes["Hide"] = "Hide";
})(InteractionsTypes || (InteractionsTypes = {}));
const InteractionsContext = /*#__PURE__*/React.createContext({});
const InteractionsProvider = ({
  onComponentInteraction,
  children
}) => {
  const onComponentClickRef = useRef(onComponentInteraction);
  onComponentClickRef.current = onComponentInteraction;
  const providerValue = useMemo(() => {
    const callback = options => {
      var _a;
      (_a = onComponentClickRef.current) === null || _a === void 0 ? void 0 : _a.call(onComponentClickRef, options);
    };
    return {
      onComponentInteraction: callback
    };
  }, []);
  useEffect(() => {
    // @ts-ignore
    window.analytics = window.analytics || {};
    // @ts-ignore
    window.analytics.onComponentClick = providerValue.onComponentInteraction;
    return () => {
      // @ts-ignore
      window.analytics = window.analytics || {};
      // @ts-ignore
      window.analytics.onComponentClick = null;
    };
  }, [providerValue]);
  return jsx(InteractionsContext.Provider, {
    value: providerValue,
    children: children
  });
};

const CustomizationsContext = /*#__PURE__*/React.createContext({
  components: {},
  behaviors: {}
});
const CustomizationsProvider = props => {
  const providerValue = useMemo(() => {
    const components = props.components || {};
    const behaviors = props.behaviors || {};
    return {
      components,
      behaviors
    };
  }, [props.components]);
  return jsx(CustomizationsContext.Provider, {
    value: providerValue,
    children: props.children
  });
};

const muiComponentsList = {
  [MuiComponents.button]: ['variant', 'size', 'color', 'loading', 'disabled']
};
const bitsComponentsList = {
  [BitsComponents.Button]: ['variant', 'size', 'color', 'loading', 'disabled']
};

const useGlobalClassesForComponentsProps = (key, props) => {
  const {
    components: componentsCustomizations
  } = useContext(CustomizationsContext);
  const createDepsFromProps = (key, props) => {
    var _a, _b, _c, _d;
    return (_d = (_c = (_b = (_a = componentsCustomizations === null || componentsCustomizations === void 0 ? void 0 : componentsCustomizations[key]) === null || _a === void 0 ? void 0 : _a.globalClasses) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.map(prop => {
      return (props === null || props === void 0 ? void 0 : props[prop]) || '';
    })) !== null && _d !== void 0 ? _d : [];
  };
  return useMemo(() => {
    var _a, _b;
    const componentGlobalClasses = (_a = componentsCustomizations === null || componentsCustomizations === void 0 ? void 0 : componentsCustomizations[key]) === null || _a === void 0 ? void 0 : _a.globalClasses;
    const listOfProps = ((_b = componentGlobalClasses === null || componentGlobalClasses === void 0 ? void 0 : componentGlobalClasses.exposeAll) !== null && _b !== void 0 ? _b : false) ? bitsComponentsList[key] : componentGlobalClasses === null || componentGlobalClasses === void 0 ? void 0 : componentGlobalClasses.props;
    const propsClasses = listOfProps === null || listOfProps === void 0 ? void 0 : listOfProps.map(propName => {
      const propValue = props === null || props === void 0 ? void 0 : props[propName];
      if (typeof propValue === 'string' || typeof propValue === 'number') {
        return `kaltura-ds-${key}_${propName}-${propValue}`;
      } else if (typeof propValue === 'boolean' && propValue) {
        return `kaltura-ds-${key}_${propName}`;
      }
      return '';
    }).join(' ');
    return `kaltura-ds-${key}${propsClasses ? ` ${propsClasses}` : ''}`;
  }, createDepsFromProps(key, props));
};

const useMuiGlobalClassesForComponentsProps = (key, props) => {
  const {
    components: componentsCustomizations
  } = useContext(CustomizationsContext);
  const createDepsFromProps = (key, props) => {
    var _a, _b, _c, _d;
    return (_d = (_c = (_b = (_a = componentsCustomizations === null || componentsCustomizations === void 0 ? void 0 : componentsCustomizations[key]) === null || _a === void 0 ? void 0 : _a.globalClasses) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.map(prop => {
      return (props === null || props === void 0 ? void 0 : props[prop]) || '';
    })) !== null && _d !== void 0 ? _d : [];
  };
  return useMemo(() => {
    var _a, _b;
    const componentGlobalClasses = (_a = componentsCustomizations === null || componentsCustomizations === void 0 ? void 0 : componentsCustomizations[key]) === null || _a === void 0 ? void 0 : _a.globalClasses;
    const listOfProps = ((_b = componentGlobalClasses === null || componentGlobalClasses === void 0 ? void 0 : componentGlobalClasses.exposeAll) !== null && _b !== void 0 ? _b : false) ? muiComponentsList[key] : componentGlobalClasses === null || componentGlobalClasses === void 0 ? void 0 : componentGlobalClasses.props;
    const propsClasses = listOfProps === null || listOfProps === void 0 ? void 0 : listOfProps.map(propName => {
      const propValue = props === null || props === void 0 ? void 0 : props[propName];
      if (typeof propValue === 'string' || typeof propValue === 'number') {
        return `kaltura-ds-${key}_${propName}-${propValue}`;
      } else if (typeof propValue === 'boolean' && propValue) {
        return `kaltura-ds-${key}_${propName}`;
      }
      return '';
    }).join(' ');
    return `kaltura-ds-${key}${propsClasses ? ` ${propsClasses}` : ''}`;
  }, createDepsFromProps(key, props));
};

/**
 * list of timezone names and their abbreviations,
 * timezone: [standard time, summertime]
 */
const zoneLookup = {
  'Africa/Abidjan': ['GMT'],
  'Africa/Accra': ['GMT'],
  'Africa/Addis_Ababa': ['EAT'],
  'Africa/Algiers': ['CET'],
  'Africa/Asmara': ['EAT'],
  'Africa/Asmera': ['EAT'],
  'Africa/Bamako': ['GMT'],
  'Africa/Bangui': ['WAT'],
  'Africa/Banjul': ['GMT'],
  'Africa/Bissau': ['GMT'],
  'Africa/Blantyre': ['CAT'],
  'Africa/Brazzaville': ['WAT'],
  'Africa/Bujumbura': ['CAT'],
  'Africa/Cairo': ['EET', 'EEST'],
  'Africa/Ceuta': ['CET', 'CEST'],
  'Africa/Conakry': ['GMT'],
  'Africa/Dakar': ['GMT'],
  'Africa/Dar_es_Salaam': ['EAT'],
  'Africa/Djibouti': ['EAT'],
  'Africa/Douala': ['WAT'],
  'Africa/Freetown': ['GMT'],
  'Africa/Gaborone': ['CAT'],
  'Africa/Harare': ['CAT'],
  'Africa/Johannesburg': ['SAST'],
  'Africa/Juba': ['CAT'],
  'Africa/Kampala': ['EAT'],
  'Africa/Khartoum': ['CAT'],
  'Africa/Kigali': ['CAT'],
  'Africa/Kinshasa': ['WAT'],
  'Africa/Lagos': ['WAT'],
  'Africa/Libreville': ['WAT'],
  'Africa/Lome': ['GMT'],
  'Africa/Luanda': ['WAT'],
  'Africa/Lubumbashi': ['CAT'],
  'Africa/Lusaka': ['CAT'],
  'Africa/Malabo': ['WAT'],
  'Africa/Maputo': ['CAT'],
  'Africa/Maseru': ['SAST'],
  'Africa/Mbabane': ['SAST'],
  'Africa/Mogadishu': ['EAT'],
  'Africa/Monrovia': ['GMT'],
  'Africa/Nairobi': ['EAT'],
  'Africa/Ndjamena': ['WAT'],
  'Africa/Niamey': ['WAT'],
  'Africa/Nouakchott': ['GMT'],
  'Africa/Ouagadougou': ['GMT'],
  'Africa/Porto-Novo': ['WAT'],
  'Africa/Sao_Tome': ['GMT'],
  'Africa/Timbuktu': ['GMT'],
  'Africa/Tripoli': ['EET'],
  'Africa/Tunis': ['CET'],
  'Africa/Windhoek': ['CAT'],
  'America/Adak': ['HST', 'HDT'],
  'America/Anchorage': ['AKST', 'AKDT'],
  'America/Anguilla': ['AST'],
  'America/Antigua': ['AST'],
  'America/Aruba': ['AST'],
  'America/Atikokan': ['EST'],
  'America/Atka': ['HST', 'HDT'],
  'America/Bahia_Banderas': ['CST'],
  'America/Barbados': ['AST'],
  'America/Belize': ['CST'],
  'America/Blanc-Sablon': ['AST'],
  'America/Boise': ['MST', 'MDT'],
  'America/Cambridge_Bay': ['MST', 'MDT'],
  'America/Cancun': ['EST'],
  'America/Cayman': ['EST'],
  'America/Chicago': ['CST', 'CDT'],
  'America/Chihuahua': ['CST'],
  'America/Ciudad_Juarez': ['MST', 'MDT'],
  'America/Coral_Harbour': ['EST'],
  'America/Costa_Rica': ['CST'],
  'America/Creston': ['MST'],
  'America/Curacao': ['AST'],
  'America/Danmarkshavn': ['GMT'],
  'America/Dawson': ['MST'],
  'America/Dawson_Creek': ['MST'],
  'America/Denver': ['MST', 'MDT'],
  'America/Detroit': ['EST', 'EDT'],
  'America/Dominica': ['AST'],
  'America/Edmonton': ['MST', 'MDT'],
  'America/El_Salvador': ['CST'],
  'America/Ensenada': ['PST', 'PDT'],
  'America/Fort_Nelson': ['MST'],
  'America/Fort_Wayne': ['EST', 'EDT'],
  'America/Glace_Bay': ['AST', 'ADT'],
  'America/Goose_Bay': ['AST', 'ADT'],
  'America/Grand_Turk': ['EST', 'EDT'],
  'America/Grenada': ['AST'],
  'America/Guadeloupe': ['AST'],
  'America/Guatemala': ['CST'],
  'America/Halifax': ['AST', 'ADT'],
  'America/Havana': ['CST', 'CDT'],
  'America/Hermosillo': ['MST'],
  'America/Indiana/Indianapolis': ['EST', 'EDT'],
  'America/Indiana/Knox': ['CST', 'CDT'],
  'America/Indiana/Marengo': ['EST', 'EDT'],
  'America/Indiana/Petersburg': ['EST', 'EDT'],
  'America/Indiana/Tell_City': ['CST', 'CDT'],
  'America/Indiana/Vevay': ['EST', 'EDT'],
  'America/Indiana/Vincennes': ['EST', 'EDT'],
  'America/Indiana/Winamac': ['EST', 'EDT'],
  'America/Indianapolis': ['EST', 'EDT'],
  'America/Inuvik': ['MST', 'MDT'],
  'America/Iqaluit': ['EST', 'EDT'],
  'America/Jamaica': ['EST'],
  'America/Juneau': ['AKST', 'AKDT'],
  'America/Kentucky/Louisville': ['EST', 'EDT'],
  'America/Kentucky/Monticello': ['EST', 'EDT'],
  'America/Knox_IN': ['CST', 'CDT'],
  'America/Kralendijk': ['AST'],
  'America/Los_Angeles': ['PST', 'PDT'],
  'America/Louisville': ['EST', 'EDT'],
  'America/Lower_Princes': ['AST'],
  'America/Managua': ['CST'],
  'America/Marigot': ['AST'],
  'America/Martinique': ['AST'],
  'America/Matamoros': ['CST', 'CDT'],
  'America/Mazatlan': ['MST'],
  'America/Menominee': ['CST', 'CDT'],
  'America/Merida': ['CST'],
  'America/Metlakatla': ['AKST', 'AKDT'],
  'America/Mexico_City': ['CST'],
  'America/Moncton': ['AST', 'ADT'],
  'America/Monterrey': ['CST'],
  'America/Montreal': ['EST', 'EDT'],
  'America/Montserrat': ['AST'],
  'America/Nassau': ['EST', 'EDT'],
  'America/New_York': ['EST', 'EDT'],
  'America/Nipigon': ['EST', 'EDT'],
  'America/Nome': ['AKST', 'AKDT'],
  'America/North_Dakota/Beulah': ['CST', 'CDT'],
  'America/North_Dakota/Center': ['CST', 'CDT'],
  'America/North_Dakota/New_Salem': ['CST', 'CDT'],
  'America/Ojinaga': ['CST', 'CDT'],
  'America/Panama': ['EST'],
  'America/Pangnirtung': ['EST', 'EDT'],
  'America/Phoenix': ['MST'],
  'America/Port-au-Prince': ['EST', 'EDT'],
  'America/Port_of_Spain': ['AST'],
  'America/Puerto_Rico': ['AST'],
  'America/Rainy_River': ['CST', 'CDT'],
  'America/Rankin_Inlet': ['CST', 'CDT'],
  'America/Regina': ['CST'],
  'America/Resolute': ['CST', 'CDT'],
  'America/Santa_Isabel': ['PST', 'PDT'],
  'America/Santo_Domingo': ['AST'],
  'America/Shiprock': ['MST', 'MDT'],
  'America/Sitka': ['AKST', 'AKDT'],
  'America/St_Barthelemy': ['AST'],
  'America/St_Johns': ['NST', 'NDT'],
  'America/St_Kitts': ['AST'],
  'America/St_Lucia': ['AST'],
  'America/St_Thomas': ['AST'],
  'America/St_Vincent': ['AST'],
  'America/Swift_Current': ['CST'],
  'America/Tegucigalpa': ['CST'],
  'America/Thule': ['AST', 'ADT'],
  'America/Thunder_Bay': ['EST', 'EDT'],
  'America/Tijuana': ['PST', 'PDT'],
  'America/Toronto': ['EST', 'EDT'],
  'America/Tortola': ['AST'],
  'America/Vancouver': ['PST', 'PDT'],
  'America/Virgin': ['AST'],
  'America/Whitehorse': ['MST'],
  'America/Winnipeg': ['CST', 'CDT'],
  'America/Yakutat': ['AKST', 'AKDT'],
  'America/Yellowknife': ['MST', 'MDT'],
  'Antarctica/Macquarie': ['AEST', 'AEDT'],
  'Antarctica/McMurdo': ['NZST', 'NZDT'],
  'Antarctica/South_Pole': ['NZST', 'NZDT'],
  'Arctic/Longyearbyen': ['CET', 'CEST'],
  'Asia/Beirut': ['EET', 'EEST'],
  'Asia/Calcutta': ['IST'],
  'Asia/Chongqing': ['CST'],
  'Asia/Chungking': ['CST'],
  'Asia/Famagusta': ['EET', 'EEST'],
  'Asia/Gaza': ['EET', 'EEST'],
  'Asia/Harbin': ['CST'],
  'Asia/Hebron': ['EET', 'EEST'],
  'Asia/Hong_Kong': ['HKT'],
  'Asia/Jakarta': ['WIB'],
  'Asia/Jayapura': ['WIT'],
  'Asia/Jerusalem': ['IST', 'IDT'],
  'Asia/Karachi': ['PKT'],
  'Asia/Kolkata': ['IST'],
  'Asia/Macao': ['CST'],
  'Asia/Macau': ['CST'],
  'Asia/Makassar': ['WITA'],
  'Asia/Manila': ['PHT'],
  'Asia/Nicosia': ['EET', 'EEST'],
  'Asia/Pontianak': ['WIB'],
  'Asia/Pyongyang': ['KST'],
  'Asia/Seoul': ['KST'],
  'Asia/Shanghai': ['CST'],
  'Asia/Taipei': ['CST'],
  'Asia/Tel_Aviv': ['IST', 'IDT'],
  'Asia/Tokyo': ['JST'],
  'Asia/Ujung_Pandang': ['WITA'],
  'Atlantic/Bermuda': ['AST', 'ADT'],
  'Atlantic/Canary': ['WET', 'WEST'],
  'Atlantic/Faeroe': ['WET', 'WEST'],
  'Atlantic/Faroe': ['WET', 'WEST'],
  'Atlantic/Jan_Mayen': ['CET', 'CEST'],
  'Atlantic/Madeira': ['WET', 'WEST'],
  'Atlantic/Reykjavik': ['GMT'],
  'Atlantic/St_Helena': ['GMT'],
  'Australia/ACT': ['AEST', 'AEDT'],
  'Australia/Adelaide': ['ACST', 'ACDT'],
  'Australia/Brisbane': ['AEST'],
  'Australia/Broken_Hill': ['ACST', 'ACDT'],
  'Australia/Canberra': ['AEST', 'AEDT'],
  'Australia/Currie': ['AEST', 'AEDT'],
  'Australia/Darwin': ['ACST'],
  'Australia/Hobart': ['AEST', 'AEDT'],
  'Australia/Lindeman': ['AEST'],
  'Australia/Melbourne': ['AEST', 'AEDT'],
  'Australia/North': ['ACST'],
  'Australia/NSW': ['AEST', 'AEDT'],
  'Australia/Perth': ['AWST'],
  'Australia/Queensland': ['AEST'],
  'Australia/South': ['ACST', 'ACDT'],
  'Australia/Sydney': ['AEST', 'AEDT'],
  'Australia/Tasmania': ['AEST', 'AEDT'],
  'Australia/Victoria': ['AEST', 'AEDT'],
  'Australia/West': ['AWST'],
  'Australia/Yancowinna': ['ACST', 'ACDT'],
  'Canada/Atlantic': ['AST', 'ADT'],
  'Canada/Central': ['CST', 'CDT'],
  'Canada/Eastern': ['EST', 'EDT'],
  'Canada/Mountain': ['MST', 'MDT'],
  'Canada/Newfoundland': ['NST', 'NDT'],
  'Canada/Pacific': ['PST', 'PDT'],
  'Canada/Saskatchewan': ['CST'],
  'Canada/Yukon': ['MST'],
  'CET': ['CET', 'CEST'],
  'Cuba': ['CST', 'CDT'],
  'EET': ['EET', 'EEST'],
  'Egypt': ['EET', 'EEST'],
  'Eire': ['IST', 'GMT'],
  'EST': ['EST'],
  'Etc/GMT': ['GMT'],
  'Etc/Greenwich': ['GMT'],
  'Etc/UCT': ['UTC'],
  'Etc/Universal': ['UTC'],
  'Etc/UTC': ['UTC'],
  'Etc/Zulu': ['UTC'],
  'Europe/Amsterdam': ['CET', 'CEST'],
  'Europe/Andorra': ['CET', 'CEST'],
  'Europe/Athens': ['EET', 'EEST'],
  'Europe/Belfast': ['GMT', 'BST'],
  'Europe/Belgrade': ['CET', 'CEST'],
  'Europe/Berlin': ['CET', 'CEST'],
  'Europe/Bratislava': ['CET', 'CEST'],
  'Europe/Brussels': ['CET', 'CEST'],
  'Europe/Bucharest': ['EET', 'EEST'],
  'Europe/Budapest': ['CET', 'CEST'],
  'Europe/Busingen': ['CET', 'CEST'],
  'Europe/Chisinau': ['EET', 'EEST'],
  'Europe/Copenhagen': ['CET', 'CEST'],
  'Europe/Dublin': ['IST', 'GMT'],
  'Europe/Gibraltar': ['CET', 'CEST'],
  'Europe/Guernsey': ['GMT', 'BST'],
  'Europe/Helsinki': ['EET', 'EEST'],
  'Europe/Isle_of_Man': ['GMT', 'BST'],
  'Europe/Jersey': ['GMT', 'BST'],
  'Europe/Kaliningrad': ['EET'],
  'Europe/Kiev': ['EET', 'EEST'],
  'Europe/Kirov': ['MSK'],
  'Europe/Kyiv': ['EET', 'EEST'],
  'Europe/Lisbon': ['WET', 'WEST'],
  'Europe/Ljubljana': ['CET', 'CEST'],
  'Europe/London': ['GMT', 'BST'],
  'Europe/Luxembourg': ['CET', 'CEST'],
  'Europe/Madrid': ['CET', 'CEST'],
  'Europe/Malta': ['CET', 'CEST'],
  'Europe/Mariehamn': ['EET', 'EEST'],
  'Europe/Monaco': ['CET', 'CEST'],
  'Europe/Moscow': ['MSK'],
  'Europe/Nicosia': ['EET', 'EEST'],
  'Europe/Oslo': ['CET', 'CEST'],
  'Europe/Paris': ['CET', 'CEST'],
  'Europe/Podgorica': ['CET', 'CEST'],
  'Europe/Prague': ['CET', 'CEST'],
  'Europe/Riga': ['EET', 'EEST'],
  'Europe/Rome': ['CET', 'CEST'],
  'Europe/San_Marino': ['CET', 'CEST'],
  'Europe/Sarajevo': ['CET', 'CEST'],
  'Europe/Simferopol': ['MSK'],
  'Europe/Skopje': ['CET', 'CEST'],
  'Europe/Sofia': ['EET', 'EEST'],
  'Europe/Stockholm': ['CET', 'CEST'],
  'Europe/Tallinn': ['EET', 'EEST'],
  'Europe/Tirane': ['CET', 'CEST'],
  'Europe/Tiraspol': ['EET', 'EEST'],
  'Europe/Uzhgorod': ['EET', 'EEST'],
  'Europe/Vaduz': ['CET', 'CEST'],
  'Europe/Vatican': ['CET', 'CEST'],
  'Europe/Vienna': ['CET', 'CEST'],
  'Europe/Vilnius': ['EET', 'EEST'],
  'Europe/Volgograd': ['MSK'],
  'Europe/Warsaw': ['CET', 'CEST'],
  'Europe/Zagreb': ['CET', 'CEST'],
  'Europe/Zaporozhye': ['EET', 'EEST'],
  'Europe/Zurich': ['CET', 'CEST'],
  'GB': ['GMT', 'BST'],
  'GB-Eire': ['GMT', 'BST'],
  'GMT': ['GMT'],
  'Greenwich': ['GMT'],
  'Hongkong': ['HKT'],
  'HST': ['HST'],
  'Iceland': ['GMT'],
  'Indian/Antananarivo': ['EAT'],
  'Indian/Comoro': ['EAT'],
  'Indian/Mayotte': ['EAT'],
  'Israel': ['IST', 'IDT'],
  'Jamaica': ['EST'],
  'Japan': ['JST'],
  'Libya': ['EET'],
  'MET': ['MET', 'MEST'],
  'Mexico/BajaNorte': ['PST', 'PDT'],
  'Mexico/BajaSur': ['MST'],
  'Mexico/General': ['CST'],
  'MST': ['MST'],
  'Navajo': ['MST', 'MDT'],
  'NZ': ['NZST', 'NZDT'],
  'Pacific/Auckland': ['NZST', 'NZDT'],
  'Pacific/Honolulu': ['HST'],
  'Pacific/Johnston': ['HST'],
  'Pacific/Midway': ['SST'],
  'Pacific/Pago_Pago': ['SST'],
  'Pacific/Saipan': ['CHST'],
  'Pacific/Samoa': ['SST'],
  'Poland': ['CET', 'CEST'],
  'Portugal': ['WET', 'WEST'],
  'PRC': ['CST'],
  'ROC': ['CST'],
  'ROK': ['KST'],
  'UCT': ['UTC'],
  'Universal': ['UTC'],
  'US/Alaska': ['AKST', 'AKDT'],
  'US/Aleutian': ['HST', 'HDT'],
  'US/Arizona': ['MST'],
  'US/Central': ['CST', 'CDT'],
  'US/East-Indiana': ['EST', 'EDT'],
  'US/Eastern': ['EST', 'EDT'],
  'US/Hawaii': ['HST'],
  'US/Indiana-Starke': ['CST', 'CDT'],
  'US/Michigan': ['EST', 'EDT'],
  'US/Mountain': ['MST', 'MDT'],
  'US/Pacific': ['PST', 'PDT'],
  'US/Samoa': ['SST'],
  'UTC': ['UTC'],
  'W-SU': ['MSK'],
  'WET': ['WET', 'WEST'],
  'Zulu': ['UTC']
};
/**
 * is the given date in Daylight Saving Time
 * @param date
 * @param timezone
 */
function isDaylightSaving(date, timezone) {
  // what we want to check
  const targetDate = new Date(date.toLocaleString('en-US', {
    timeZone: timezone
  })); // the locale doesn't matter, only the timezone
  const targetOffset = targetDate.getTimezoneOffset();
  const jan = new Date(targetDate.getFullYear(), 0, 1);
  const jul = new Date(targetDate.getFullYear(), 6, 1);
  const dstOffset = Math.min(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  return targetOffset === dstOffset;
}
/**
 * dayjs plugin to support timezone names abbreviations
 * when formatting, ie IST, IDT, CET
 *
 * this plugin depends on dayjs's timezone plugin,
 * which in turn depends on utc plugin.
 *
 * to use, load and extend like other dayjs plugins:
 * import { timezoneAbbr } from "@kaltura/ds-react-utils";
 * dayjs.extend(timezoneAbbr);
 */
const timezoneAbbr = (o, c, d) => {
  const proto = c.prototype;
  const oldOffsetName = proto.offsetName;
  proto.offsetName = function (type) {
    // only if asked for short name
    if (!type || type === 'short') {
      const zone = this.$x.$timezone || d.tz.guess();
      if (zoneLookup[zone]) {
        // we have data, is dst on?
        const isDSTon = isDaylightSaving(this.toDate(), zone);
        if (isDSTon && zoneLookup[zone].length > 1) {
          // we have DST data and we need it
          return zoneLookup[zone][1];
        }
        // return non DST name
        return zoneLookup[zone][0];
      }
    }
    return oldOffsetName.call(this, type);
  };
};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

var check = function (it) {
  return it && it.Math == Math && it;
};

// https://github.com/zloirock/core-js/issues/86#issuecomment-115759028
var global$f =
  // eslint-disable-next-line es/no-global-this -- safe
  check(typeof globalThis == 'object' && globalThis) ||
  check(typeof window == 'object' && window) ||
  // eslint-disable-next-line no-restricted-globals -- safe
  check(typeof self == 'object' && self) ||
  check(typeof commonjsGlobal == 'object' && commonjsGlobal) ||
  // eslint-disable-next-line no-new-func -- fallback
  (function () { return this; })() || commonjsGlobal || Function('return this')();

var objectGetOwnPropertyDescriptor = {};

var fails$e = function (exec) {
  try {
    return !!exec();
  } catch (error) {
    return true;
  }
};

var fails$d = fails$e;

// Detect IE8's incomplete defineProperty implementation
var descriptors = !fails$d(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty({}, 1, { get: function () { return 7; } })[1] != 7;
});

var fails$c = fails$e;

var functionBindNative = !fails$c(function () {
  // eslint-disable-next-line es/no-function-prototype-bind -- safe
  var test = (function () { /* empty */ }).bind();
  // eslint-disable-next-line no-prototype-builtins -- safe
  return typeof test != 'function' || test.hasOwnProperty('prototype');
});

var NATIVE_BIND$2 = functionBindNative;

var call$8 = Function.prototype.call;

var functionCall = NATIVE_BIND$2 ? call$8.bind(call$8) : function () {
  return call$8.apply(call$8, arguments);
};

var objectPropertyIsEnumerable = {};

var $propertyIsEnumerable = {}.propertyIsEnumerable;
// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var getOwnPropertyDescriptor$1 = Object.getOwnPropertyDescriptor;

// Nashorn ~ JDK8 bug
var NASHORN_BUG = getOwnPropertyDescriptor$1 && !$propertyIsEnumerable.call({ 1: 2 }, 1);

// `Object.prototype.propertyIsEnumerable` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.propertyisenumerable
objectPropertyIsEnumerable.f = NASHORN_BUG ? function propertyIsEnumerable(V) {
  var descriptor = getOwnPropertyDescriptor$1(this, V);
  return !!descriptor && descriptor.enumerable;
} : $propertyIsEnumerable;

var createPropertyDescriptor$2 = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};

var NATIVE_BIND$1 = functionBindNative;

var FunctionPrototype$2 = Function.prototype;
var call$7 = FunctionPrototype$2.call;
var uncurryThisWithBind = NATIVE_BIND$1 && FunctionPrototype$2.bind.bind(call$7, call$7);

var functionUncurryThis = NATIVE_BIND$1 ? uncurryThisWithBind : function (fn) {
  return function () {
    return call$7.apply(fn, arguments);
  };
};

var uncurryThis$g = functionUncurryThis;

var toString$7 = uncurryThis$g({}.toString);
var stringSlice$5 = uncurryThis$g(''.slice);

var classofRaw$2 = function (it) {
  return stringSlice$5(toString$7(it), 8, -1);
};

var uncurryThis$f = functionUncurryThis;
var fails$b = fails$e;
var classof$3 = classofRaw$2;

var $Object$3 = Object;
var split = uncurryThis$f(''.split);

// fallback for non-array-like ES3 and non-enumerable old V8 strings
var indexedObject = fails$b(function () {
  // throws an error in rhino, see https://github.com/mozilla/rhino/issues/346
  // eslint-disable-next-line no-prototype-builtins -- safe
  return !$Object$3('z').propertyIsEnumerable(0);
}) ? function (it) {
  return classof$3(it) == 'String' ? split(it, '') : $Object$3(it);
} : $Object$3;

// we can't use just `it == null` since of `document.all` special case
// https://tc39.es/ecma262/#sec-IsHTMLDDA-internal-slot-aec
var isNullOrUndefined$3 = function (it) {
  return it === null || it === undefined;
};

var isNullOrUndefined$2 = isNullOrUndefined$3;

var $TypeError$6 = TypeError;

// `RequireObjectCoercible` abstract operation
// https://tc39.es/ecma262/#sec-requireobjectcoercible
var requireObjectCoercible$5 = function (it) {
  if (isNullOrUndefined$2(it)) throw $TypeError$6("Can't call method on " + it);
  return it;
};

// toObject with fallback for non-array-like ES3 strings
var IndexedObject = indexedObject;
var requireObjectCoercible$4 = requireObjectCoercible$5;

var toIndexedObject$4 = function (it) {
  return IndexedObject(requireObjectCoercible$4(it));
};

var documentAll$2 = typeof document == 'object' && document.all;

// https://tc39.es/ecma262/#sec-IsHTMLDDA-internal-slot
// eslint-disable-next-line unicorn/no-typeof-undefined -- required for testing
var IS_HTMLDDA = typeof documentAll$2 == 'undefined' && documentAll$2 !== undefined;

var documentAll_1 = {
  all: documentAll$2,
  IS_HTMLDDA: IS_HTMLDDA
};

var $documentAll$1 = documentAll_1;

var documentAll$1 = $documentAll$1.all;

// `IsCallable` abstract operation
// https://tc39.es/ecma262/#sec-iscallable
var isCallable$d = $documentAll$1.IS_HTMLDDA ? function (argument) {
  return typeof argument == 'function' || argument === documentAll$1;
} : function (argument) {
  return typeof argument == 'function';
};

var isCallable$c = isCallable$d;
var $documentAll = documentAll_1;

var documentAll = $documentAll.all;

var isObject$5 = $documentAll.IS_HTMLDDA ? function (it) {
  return typeof it == 'object' ? it !== null : isCallable$c(it) || it === documentAll;
} : function (it) {
  return typeof it == 'object' ? it !== null : isCallable$c(it);
};

var global$e = global$f;
var isCallable$b = isCallable$d;

var aFunction = function (argument) {
  return isCallable$b(argument) ? argument : undefined;
};

var getBuiltIn$3 = function (namespace, method) {
  return arguments.length < 2 ? aFunction(global$e[namespace]) : global$e[namespace] && global$e[namespace][method];
};

var uncurryThis$e = functionUncurryThis;

var objectIsPrototypeOf = uncurryThis$e({}.isPrototypeOf);

var engineUserAgent = typeof navigator != 'undefined' && String(navigator.userAgent) || '';

var global$d = global$f;
var userAgent = engineUserAgent;

var process = global$d.process;
var Deno = global$d.Deno;
var versions = process && process.versions || Deno && Deno.version;
var v8 = versions && versions.v8;
var match, version;

if (v8) {
  match = v8.split('.');
  // in old Chrome, versions of V8 isn't V8 = Chrome / 10
  // but their correct versions are not interesting for us
  version = match[0] > 0 && match[0] < 4 ? 1 : +(match[0] + match[1]);
}

// BrowserFS NodeJS `process` polyfill incorrectly set `.v8` to `0.0`
// so check `userAgent` even if `.v8` exists, but 0
if (!version && userAgent) {
  match = userAgent.match(/Edge\/(\d+)/);
  if (!match || match[1] >= 74) {
    match = userAgent.match(/Chrome\/(\d+)/);
    if (match) version = +match[1];
  }
}

var engineV8Version = version;

/* eslint-disable es/no-symbol -- required for testing */

var V8_VERSION = engineV8Version;
var fails$a = fails$e;
var global$c = global$f;

var $String$4 = global$c.String;

// eslint-disable-next-line es/no-object-getownpropertysymbols -- required for testing
var symbolConstructorDetection = !!Object.getOwnPropertySymbols && !fails$a(function () {
  var symbol = Symbol();
  // Chrome 38 Symbol has incorrect toString conversion
  // `get-own-property-symbols` polyfill symbols converted to object are not Symbol instances
  // nb: Do not call `String` directly to avoid this being optimized out to `symbol+''` which will,
  // of course, fail.
  return !$String$4(symbol) || !(Object(symbol) instanceof Symbol) ||
    // Chrome 38-40 symbols are not inherited from DOM collections prototypes to instances
    !Symbol.sham && V8_VERSION && V8_VERSION < 41;
});

/* eslint-disable es/no-symbol -- required for testing */

var NATIVE_SYMBOL$1 = symbolConstructorDetection;

var useSymbolAsUid = NATIVE_SYMBOL$1
  && !Symbol.sham
  && typeof Symbol.iterator == 'symbol';

var getBuiltIn$2 = getBuiltIn$3;
var isCallable$a = isCallable$d;
var isPrototypeOf = objectIsPrototypeOf;
var USE_SYMBOL_AS_UID$1 = useSymbolAsUid;

var $Object$2 = Object;

var isSymbol$2 = USE_SYMBOL_AS_UID$1 ? function (it) {
  return typeof it == 'symbol';
} : function (it) {
  var $Symbol = getBuiltIn$2('Symbol');
  return isCallable$a($Symbol) && isPrototypeOf($Symbol.prototype, $Object$2(it));
};

var $String$3 = String;

var tryToString$1 = function (argument) {
  try {
    return $String$3(argument);
  } catch (error) {
    return 'Object';
  }
};

var isCallable$9 = isCallable$d;
var tryToString = tryToString$1;

var $TypeError$5 = TypeError;

// `Assert: IsCallable(argument) is true`
var aCallable$1 = function (argument) {
  if (isCallable$9(argument)) return argument;
  throw $TypeError$5(tryToString(argument) + ' is not a function');
};

var aCallable = aCallable$1;
var isNullOrUndefined$1 = isNullOrUndefined$3;

// `GetMethod` abstract operation
// https://tc39.es/ecma262/#sec-getmethod
var getMethod$2 = function (V, P) {
  var func = V[P];
  return isNullOrUndefined$1(func) ? undefined : aCallable(func);
};

var call$6 = functionCall;
var isCallable$8 = isCallable$d;
var isObject$4 = isObject$5;

var $TypeError$4 = TypeError;

// `OrdinaryToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-ordinarytoprimitive
var ordinaryToPrimitive$1 = function (input, pref) {
  var fn, val;
  if (pref === 'string' && isCallable$8(fn = input.toString) && !isObject$4(val = call$6(fn, input))) return val;
  if (isCallable$8(fn = input.valueOf) && !isObject$4(val = call$6(fn, input))) return val;
  if (pref !== 'string' && isCallable$8(fn = input.toString) && !isObject$4(val = call$6(fn, input))) return val;
  throw $TypeError$4("Can't convert object to primitive value");
};

var shared$4 = {exports: {}};

var global$b = global$f;

// eslint-disable-next-line es/no-object-defineproperty -- safe
var defineProperty$1 = Object.defineProperty;

var defineGlobalProperty$3 = function (key, value) {
  try {
    defineProperty$1(global$b, key, { value: value, configurable: true, writable: true });
  } catch (error) {
    global$b[key] = value;
  } return value;
};

var global$a = global$f;
var defineGlobalProperty$2 = defineGlobalProperty$3;

var SHARED = '__core-js_shared__';
var store$3 = global$a[SHARED] || defineGlobalProperty$2(SHARED, {});

var sharedStore = store$3;

var store$2 = sharedStore;

(shared$4.exports = function (key, value) {
  return store$2[key] || (store$2[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: '3.30.2',
  mode: 'global',
  copyright: '© 2014-2023 Denis Pushkarev (zloirock.ru)',
  license: 'https://github.com/zloirock/core-js/blob/v3.30.2/LICENSE',
  source: 'https://github.com/zloirock/core-js'
});

var requireObjectCoercible$3 = requireObjectCoercible$5;

var $Object$1 = Object;

// `ToObject` abstract operation
// https://tc39.es/ecma262/#sec-toobject
var toObject$2 = function (argument) {
  return $Object$1(requireObjectCoercible$3(argument));
};

var uncurryThis$d = functionUncurryThis;
var toObject$1 = toObject$2;

var hasOwnProperty = uncurryThis$d({}.hasOwnProperty);

// `HasOwnProperty` abstract operation
// https://tc39.es/ecma262/#sec-hasownproperty
// eslint-disable-next-line es/no-object-hasown -- safe
var hasOwnProperty_1 = Object.hasOwn || function hasOwn(it, key) {
  return hasOwnProperty(toObject$1(it), key);
};

var uncurryThis$c = functionUncurryThis;

var id = 0;
var postfix = Math.random();
var toString$6 = uncurryThis$c(1.0.toString);

var uid$2 = function (key) {
  return 'Symbol(' + (key === undefined ? '' : key) + ')_' + toString$6(++id + postfix, 36);
};

var global$9 = global$f;
var shared$3 = shared$4.exports;
var hasOwn$6 = hasOwnProperty_1;
var uid$1 = uid$2;
var NATIVE_SYMBOL = symbolConstructorDetection;
var USE_SYMBOL_AS_UID = useSymbolAsUid;

var Symbol$2 = global$9.Symbol;
var WellKnownSymbolsStore = shared$3('wks');
var createWellKnownSymbol = USE_SYMBOL_AS_UID ? Symbol$2['for'] || Symbol$2 : Symbol$2 && Symbol$2.withoutSetter || uid$1;

var wellKnownSymbol$5 = function (name) {
  if (!hasOwn$6(WellKnownSymbolsStore, name)) {
    WellKnownSymbolsStore[name] = NATIVE_SYMBOL && hasOwn$6(Symbol$2, name)
      ? Symbol$2[name]
      : createWellKnownSymbol('Symbol.' + name);
  } return WellKnownSymbolsStore[name];
};

var call$5 = functionCall;
var isObject$3 = isObject$5;
var isSymbol$1 = isSymbol$2;
var getMethod$1 = getMethod$2;
var ordinaryToPrimitive = ordinaryToPrimitive$1;
var wellKnownSymbol$4 = wellKnownSymbol$5;

var $TypeError$3 = TypeError;
var TO_PRIMITIVE = wellKnownSymbol$4('toPrimitive');

// `ToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-toprimitive
var toPrimitive$1 = function (input, pref) {
  if (!isObject$3(input) || isSymbol$1(input)) return input;
  var exoticToPrim = getMethod$1(input, TO_PRIMITIVE);
  var result;
  if (exoticToPrim) {
    if (pref === undefined) pref = 'default';
    result = call$5(exoticToPrim, input, pref);
    if (!isObject$3(result) || isSymbol$1(result)) return result;
    throw $TypeError$3("Can't convert object to primitive value");
  }
  if (pref === undefined) pref = 'number';
  return ordinaryToPrimitive(input, pref);
};

var toPrimitive = toPrimitive$1;
var isSymbol = isSymbol$2;

// `ToPropertyKey` abstract operation
// https://tc39.es/ecma262/#sec-topropertykey
var toPropertyKey$2 = function (argument) {
  var key = toPrimitive(argument, 'string');
  return isSymbol(key) ? key : key + '';
};

var global$8 = global$f;
var isObject$2 = isObject$5;

var document$1 = global$8.document;
// typeof document.createElement is 'object' in old IE
var EXISTS$1 = isObject$2(document$1) && isObject$2(document$1.createElement);

var documentCreateElement$1 = function (it) {
  return EXISTS$1 ? document$1.createElement(it) : {};
};

var DESCRIPTORS$7 = descriptors;
var fails$9 = fails$e;
var createElement = documentCreateElement$1;

// Thanks to IE8 for its funny defineProperty
var ie8DomDefine = !DESCRIPTORS$7 && !fails$9(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty(createElement('div'), 'a', {
    get: function () { return 7; }
  }).a != 7;
});

var DESCRIPTORS$6 = descriptors;
var call$4 = functionCall;
var propertyIsEnumerableModule = objectPropertyIsEnumerable;
var createPropertyDescriptor$1 = createPropertyDescriptor$2;
var toIndexedObject$3 = toIndexedObject$4;
var toPropertyKey$1 = toPropertyKey$2;
var hasOwn$5 = hasOwnProperty_1;
var IE8_DOM_DEFINE$1 = ie8DomDefine;

// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var $getOwnPropertyDescriptor$1 = Object.getOwnPropertyDescriptor;

// `Object.getOwnPropertyDescriptor` method
// https://tc39.es/ecma262/#sec-object.getownpropertydescriptor
objectGetOwnPropertyDescriptor.f = DESCRIPTORS$6 ? $getOwnPropertyDescriptor$1 : function getOwnPropertyDescriptor(O, P) {
  O = toIndexedObject$3(O);
  P = toPropertyKey$1(P);
  if (IE8_DOM_DEFINE$1) try {
    return $getOwnPropertyDescriptor$1(O, P);
  } catch (error) { /* empty */ }
  if (hasOwn$5(O, P)) return createPropertyDescriptor$1(!call$4(propertyIsEnumerableModule.f, O, P), O[P]);
};

var objectDefineProperty = {};

var DESCRIPTORS$5 = descriptors;
var fails$8 = fails$e;

// V8 ~ Chrome 36-
// https://bugs.chromium.org/p/v8/issues/detail?id=3334
var v8PrototypeDefineBug = DESCRIPTORS$5 && fails$8(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty(function () { /* empty */ }, 'prototype', {
    value: 42,
    writable: false
  }).prototype != 42;
});

var isObject$1 = isObject$5;

var $String$2 = String;
var $TypeError$2 = TypeError;

// `Assert: Type(argument) is Object`
var anObject$7 = function (argument) {
  if (isObject$1(argument)) return argument;
  throw $TypeError$2($String$2(argument) + ' is not an object');
};

var DESCRIPTORS$4 = descriptors;
var IE8_DOM_DEFINE = ie8DomDefine;
var V8_PROTOTYPE_DEFINE_BUG$1 = v8PrototypeDefineBug;
var anObject$6 = anObject$7;
var toPropertyKey = toPropertyKey$2;

var $TypeError$1 = TypeError;
// eslint-disable-next-line es/no-object-defineproperty -- safe
var $defineProperty = Object.defineProperty;
// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var $getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;
var ENUMERABLE = 'enumerable';
var CONFIGURABLE$1 = 'configurable';
var WRITABLE = 'writable';

// `Object.defineProperty` method
// https://tc39.es/ecma262/#sec-object.defineproperty
objectDefineProperty.f = DESCRIPTORS$4 ? V8_PROTOTYPE_DEFINE_BUG$1 ? function defineProperty(O, P, Attributes) {
  anObject$6(O);
  P = toPropertyKey(P);
  anObject$6(Attributes);
  if (typeof O === 'function' && P === 'prototype' && 'value' in Attributes && WRITABLE in Attributes && !Attributes[WRITABLE]) {
    var current = $getOwnPropertyDescriptor(O, P);
    if (current && current[WRITABLE]) {
      O[P] = Attributes.value;
      Attributes = {
        configurable: CONFIGURABLE$1 in Attributes ? Attributes[CONFIGURABLE$1] : current[CONFIGURABLE$1],
        enumerable: ENUMERABLE in Attributes ? Attributes[ENUMERABLE] : current[ENUMERABLE],
        writable: false
      };
    }
  } return $defineProperty(O, P, Attributes);
} : $defineProperty : function defineProperty(O, P, Attributes) {
  anObject$6(O);
  P = toPropertyKey(P);
  anObject$6(Attributes);
  if (IE8_DOM_DEFINE) try {
    return $defineProperty(O, P, Attributes);
  } catch (error) { /* empty */ }
  if ('get' in Attributes || 'set' in Attributes) throw $TypeError$1('Accessors not supported');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};

var DESCRIPTORS$3 = descriptors;
var definePropertyModule$3 = objectDefineProperty;
var createPropertyDescriptor = createPropertyDescriptor$2;

var createNonEnumerableProperty$3 = DESCRIPTORS$3 ? function (object, key, value) {
  return definePropertyModule$3.f(object, key, createPropertyDescriptor(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};

var makeBuiltIn$2 = {exports: {}};

var DESCRIPTORS$2 = descriptors;
var hasOwn$4 = hasOwnProperty_1;

var FunctionPrototype$1 = Function.prototype;
// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var getDescriptor = DESCRIPTORS$2 && Object.getOwnPropertyDescriptor;

var EXISTS = hasOwn$4(FunctionPrototype$1, 'name');
// additional protection from minified / mangled / dropped function names
var PROPER = EXISTS && (function something() { /* empty */ }).name === 'something';
var CONFIGURABLE = EXISTS && (!DESCRIPTORS$2 || (DESCRIPTORS$2 && getDescriptor(FunctionPrototype$1, 'name').configurable));

var functionName = {
  EXISTS: EXISTS,
  PROPER: PROPER,
  CONFIGURABLE: CONFIGURABLE
};

var uncurryThis$b = functionUncurryThis;
var isCallable$7 = isCallable$d;
var store$1 = sharedStore;

var functionToString = uncurryThis$b(Function.toString);

// this helper broken in `core-js@3.4.1-3.4.4`, so we can't use `shared` helper
if (!isCallable$7(store$1.inspectSource)) {
  store$1.inspectSource = function (it) {
    return functionToString(it);
  };
}

var inspectSource$1 = store$1.inspectSource;

var global$7 = global$f;
var isCallable$6 = isCallable$d;

var WeakMap$1 = global$7.WeakMap;

var weakMapBasicDetection = isCallable$6(WeakMap$1) && /native code/.test(String(WeakMap$1));

var shared$2 = shared$4.exports;
var uid = uid$2;

var keys = shared$2('keys');

var sharedKey$2 = function (key) {
  return keys[key] || (keys[key] = uid(key));
};

var hiddenKeys$4 = {};

var NATIVE_WEAK_MAP = weakMapBasicDetection;
var global$6 = global$f;
var isObject = isObject$5;
var createNonEnumerableProperty$2 = createNonEnumerableProperty$3;
var hasOwn$3 = hasOwnProperty_1;
var shared$1 = sharedStore;
var sharedKey$1 = sharedKey$2;
var hiddenKeys$3 = hiddenKeys$4;

var OBJECT_ALREADY_INITIALIZED = 'Object already initialized';
var TypeError$1 = global$6.TypeError;
var WeakMap = global$6.WeakMap;
var set, get, has;

var enforce = function (it) {
  return has(it) ? get(it) : set(it, {});
};

var getterFor = function (TYPE) {
  return function (it) {
    var state;
    if (!isObject(it) || (state = get(it)).type !== TYPE) {
      throw TypeError$1('Incompatible receiver, ' + TYPE + ' required');
    } return state;
  };
};

if (NATIVE_WEAK_MAP || shared$1.state) {
  var store = shared$1.state || (shared$1.state = new WeakMap());
  /* eslint-disable no-self-assign -- prototype methods protection */
  store.get = store.get;
  store.has = store.has;
  store.set = store.set;
  /* eslint-enable no-self-assign -- prototype methods protection */
  set = function (it, metadata) {
    if (store.has(it)) throw TypeError$1(OBJECT_ALREADY_INITIALIZED);
    metadata.facade = it;
    store.set(it, metadata);
    return metadata;
  };
  get = function (it) {
    return store.get(it) || {};
  };
  has = function (it) {
    return store.has(it);
  };
} else {
  var STATE = sharedKey$1('state');
  hiddenKeys$3[STATE] = true;
  set = function (it, metadata) {
    if (hasOwn$3(it, STATE)) throw TypeError$1(OBJECT_ALREADY_INITIALIZED);
    metadata.facade = it;
    createNonEnumerableProperty$2(it, STATE, metadata);
    return metadata;
  };
  get = function (it) {
    return hasOwn$3(it, STATE) ? it[STATE] : {};
  };
  has = function (it) {
    return hasOwn$3(it, STATE);
  };
}

var internalState = {
  set: set,
  get: get,
  has: has,
  enforce: enforce,
  getterFor: getterFor
};

var uncurryThis$a = functionUncurryThis;
var fails$7 = fails$e;
var isCallable$5 = isCallable$d;
var hasOwn$2 = hasOwnProperty_1;
var DESCRIPTORS$1 = descriptors;
var CONFIGURABLE_FUNCTION_NAME = functionName.CONFIGURABLE;
var inspectSource = inspectSource$1;
var InternalStateModule = internalState;

var enforceInternalState = InternalStateModule.enforce;
var getInternalState$1 = InternalStateModule.get;
var $String$1 = String;
// eslint-disable-next-line es/no-object-defineproperty -- safe
var defineProperty = Object.defineProperty;
var stringSlice$4 = uncurryThis$a(''.slice);
var replace$3 = uncurryThis$a(''.replace);
var join = uncurryThis$a([].join);

var CONFIGURABLE_LENGTH = DESCRIPTORS$1 && !fails$7(function () {
  return defineProperty(function () { /* empty */ }, 'length', { value: 8 }).length !== 8;
});

var TEMPLATE = String(String).split('String');

var makeBuiltIn$1 = makeBuiltIn$2.exports = function (value, name, options) {
  if (stringSlice$4($String$1(name), 0, 7) === 'Symbol(') {
    name = '[' + replace$3($String$1(name), /^Symbol\(([^)]*)\)/, '$1') + ']';
  }
  if (options && options.getter) name = 'get ' + name;
  if (options && options.setter) name = 'set ' + name;
  if (!hasOwn$2(value, 'name') || (CONFIGURABLE_FUNCTION_NAME && value.name !== name)) {
    if (DESCRIPTORS$1) defineProperty(value, 'name', { value: name, configurable: true });
    else value.name = name;
  }
  if (CONFIGURABLE_LENGTH && options && hasOwn$2(options, 'arity') && value.length !== options.arity) {
    defineProperty(value, 'length', { value: options.arity });
  }
  try {
    if (options && hasOwn$2(options, 'constructor') && options.constructor) {
      if (DESCRIPTORS$1) defineProperty(value, 'prototype', { writable: false });
    // in V8 ~ Chrome 53, prototypes of some methods, like `Array.prototype.values`, are non-writable
    } else if (value.prototype) value.prototype = undefined;
  } catch (error) { /* empty */ }
  var state = enforceInternalState(value);
  if (!hasOwn$2(state, 'source')) {
    state.source = join(TEMPLATE, typeof name == 'string' ? name : '');
  } return value;
};

// add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
// eslint-disable-next-line no-extend-native -- required
Function.prototype.toString = makeBuiltIn$1(function toString() {
  return isCallable$5(this) && getInternalState$1(this).source || inspectSource(this);
}, 'toString');

var isCallable$4 = isCallable$d;
var definePropertyModule$2 = objectDefineProperty;
var makeBuiltIn = makeBuiltIn$2.exports;
var defineGlobalProperty$1 = defineGlobalProperty$3;

var defineBuiltIn$2 = function (O, key, value, options) {
  if (!options) options = {};
  var simple = options.enumerable;
  var name = options.name !== undefined ? options.name : key;
  if (isCallable$4(value)) makeBuiltIn(value, name, options);
  if (options.global) {
    if (simple) O[key] = value;
    else defineGlobalProperty$1(key, value);
  } else {
    try {
      if (!options.unsafe) delete O[key];
      else if (O[key]) simple = true;
    } catch (error) { /* empty */ }
    if (simple) O[key] = value;
    else definePropertyModule$2.f(O, key, {
      value: value,
      enumerable: false,
      configurable: !options.nonConfigurable,
      writable: !options.nonWritable
    });
  } return O;
};

var objectGetOwnPropertyNames = {};

var ceil = Math.ceil;
var floor$1 = Math.floor;

// `Math.trunc` method
// https://tc39.es/ecma262/#sec-math.trunc
// eslint-disable-next-line es/no-math-trunc -- safe
var mathTrunc = Math.trunc || function trunc(x) {
  var n = +x;
  return (n > 0 ? floor$1 : ceil)(n);
};

var trunc = mathTrunc;

// `ToIntegerOrInfinity` abstract operation
// https://tc39.es/ecma262/#sec-tointegerorinfinity
var toIntegerOrInfinity$4 = function (argument) {
  var number = +argument;
  // eslint-disable-next-line no-self-compare -- NaN check
  return number !== number || number === 0 ? 0 : trunc(number);
};

var toIntegerOrInfinity$3 = toIntegerOrInfinity$4;

var max$1 = Math.max;
var min$2 = Math.min;

// Helper for a popular repeating case of the spec:
// Let integer be ? ToInteger(index).
// If integer < 0, let result be max((length + integer), 0); else let result be min(integer, length).
var toAbsoluteIndex$1 = function (index, length) {
  var integer = toIntegerOrInfinity$3(index);
  return integer < 0 ? max$1(integer + length, 0) : min$2(integer, length);
};

var toIntegerOrInfinity$2 = toIntegerOrInfinity$4;

var min$1 = Math.min;

// `ToLength` abstract operation
// https://tc39.es/ecma262/#sec-tolength
var toLength$2 = function (argument) {
  return argument > 0 ? min$1(toIntegerOrInfinity$2(argument), 0x1FFFFFFFFFFFFF) : 0; // 2 ** 53 - 1 == 9007199254740991
};

var toLength$1 = toLength$2;

// `LengthOfArrayLike` abstract operation
// https://tc39.es/ecma262/#sec-lengthofarraylike
var lengthOfArrayLike$1 = function (obj) {
  return toLength$1(obj.length);
};

var toIndexedObject$2 = toIndexedObject$4;
var toAbsoluteIndex = toAbsoluteIndex$1;
var lengthOfArrayLike = lengthOfArrayLike$1;

// `Array.prototype.{ indexOf, includes }` methods implementation
var createMethod$2 = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIndexedObject$2($this);
    var length = lengthOfArrayLike(O);
    var index = toAbsoluteIndex(fromIndex, length);
    var value;
    // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare -- NaN check
    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++];
      // eslint-disable-next-line no-self-compare -- NaN check
      if (value != value) return true;
    // Array#indexOf ignores holes, Array#includes - not
    } else for (;length > index; index++) {
      if ((IS_INCLUDES || index in O) && O[index] === el) return IS_INCLUDES || index || 0;
    } return !IS_INCLUDES && -1;
  };
};

var arrayIncludes = {
  // `Array.prototype.includes` method
  // https://tc39.es/ecma262/#sec-array.prototype.includes
  includes: createMethod$2(true),
  // `Array.prototype.indexOf` method
  // https://tc39.es/ecma262/#sec-array.prototype.indexof
  indexOf: createMethod$2(false)
};

var uncurryThis$9 = functionUncurryThis;
var hasOwn$1 = hasOwnProperty_1;
var toIndexedObject$1 = toIndexedObject$4;
var indexOf$1 = arrayIncludes.indexOf;
var hiddenKeys$2 = hiddenKeys$4;

var push$1 = uncurryThis$9([].push);

var objectKeysInternal = function (object, names) {
  var O = toIndexedObject$1(object);
  var i = 0;
  var result = [];
  var key;
  for (key in O) !hasOwn$1(hiddenKeys$2, key) && hasOwn$1(O, key) && push$1(result, key);
  // Don't enum bug & hidden keys
  while (names.length > i) if (hasOwn$1(O, key = names[i++])) {
    ~indexOf$1(result, key) || push$1(result, key);
  }
  return result;
};

// IE8- don't enum bug keys
var enumBugKeys$3 = [
  'constructor',
  'hasOwnProperty',
  'isPrototypeOf',
  'propertyIsEnumerable',
  'toLocaleString',
  'toString',
  'valueOf'
];

var internalObjectKeys$1 = objectKeysInternal;
var enumBugKeys$2 = enumBugKeys$3;

var hiddenKeys$1 = enumBugKeys$2.concat('length', 'prototype');

// `Object.getOwnPropertyNames` method
// https://tc39.es/ecma262/#sec-object.getownpropertynames
// eslint-disable-next-line es/no-object-getownpropertynames -- safe
objectGetOwnPropertyNames.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return internalObjectKeys$1(O, hiddenKeys$1);
};

var objectGetOwnPropertySymbols = {};

// eslint-disable-next-line es/no-object-getownpropertysymbols -- safe
objectGetOwnPropertySymbols.f = Object.getOwnPropertySymbols;

var getBuiltIn$1 = getBuiltIn$3;
var uncurryThis$8 = functionUncurryThis;
var getOwnPropertyNamesModule = objectGetOwnPropertyNames;
var getOwnPropertySymbolsModule = objectGetOwnPropertySymbols;
var anObject$5 = anObject$7;

var concat$1 = uncurryThis$8([].concat);

// all object keys, includes non-enumerable and symbols
var ownKeys$1 = getBuiltIn$1('Reflect', 'ownKeys') || function ownKeys(it) {
  var keys = getOwnPropertyNamesModule.f(anObject$5(it));
  var getOwnPropertySymbols = getOwnPropertySymbolsModule.f;
  return getOwnPropertySymbols ? concat$1(keys, getOwnPropertySymbols(it)) : keys;
};

var hasOwn = hasOwnProperty_1;
var ownKeys = ownKeys$1;
var getOwnPropertyDescriptorModule = objectGetOwnPropertyDescriptor;
var definePropertyModule$1 = objectDefineProperty;

var copyConstructorProperties$1 = function (target, source, exceptions) {
  var keys = ownKeys(source);
  var defineProperty = definePropertyModule$1.f;
  var getOwnPropertyDescriptor = getOwnPropertyDescriptorModule.f;
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (!hasOwn(target, key) && !(exceptions && hasOwn(exceptions, key))) {
      defineProperty(target, key, getOwnPropertyDescriptor(source, key));
    }
  }
};

var fails$6 = fails$e;
var isCallable$3 = isCallable$d;

var replacement = /#|\.prototype\./;

var isForced$1 = function (feature, detection) {
  var value = data[normalize(feature)];
  return value == POLYFILL ? true
    : value == NATIVE ? false
    : isCallable$3(detection) ? fails$6(detection)
    : !!detection;
};

var normalize = isForced$1.normalize = function (string) {
  return String(string).replace(replacement, '.').toLowerCase();
};

var data = isForced$1.data = {};
var NATIVE = isForced$1.NATIVE = 'N';
var POLYFILL = isForced$1.POLYFILL = 'P';

var isForced_1 = isForced$1;

var global$5 = global$f;
var getOwnPropertyDescriptor = objectGetOwnPropertyDescriptor.f;
var createNonEnumerableProperty$1 = createNonEnumerableProperty$3;
var defineBuiltIn$1 = defineBuiltIn$2;
var defineGlobalProperty = defineGlobalProperty$3;
var copyConstructorProperties = copyConstructorProperties$1;
var isForced = isForced_1;

/*
  options.target         - name of the target object
  options.global         - target is the global object
  options.stat           - export as static methods of target
  options.proto          - export as prototype methods of target
  options.real           - real prototype method for the `pure` version
  options.forced         - export even if the native feature is available
  options.bind           - bind methods to the target, required for the `pure` version
  options.wrap           - wrap constructors to preventing global pollution, required for the `pure` version
  options.unsafe         - use the simple assignment of property instead of delete + defineProperty
  options.sham           - add a flag to not completely full polyfills
  options.enumerable     - export as enumerable property
  options.dontCallGetSet - prevent calling a getter on target
  options.name           - the .name of the function if it does not match the key
*/
var _export = function (options, source) {
  var TARGET = options.target;
  var GLOBAL = options.global;
  var STATIC = options.stat;
  var FORCED, target, key, targetProperty, sourceProperty, descriptor;
  if (GLOBAL) {
    target = global$5;
  } else if (STATIC) {
    target = global$5[TARGET] || defineGlobalProperty(TARGET, {});
  } else {
    target = (global$5[TARGET] || {}).prototype;
  }
  if (target) for (key in source) {
    sourceProperty = source[key];
    if (options.dontCallGetSet) {
      descriptor = getOwnPropertyDescriptor(target, key);
      targetProperty = descriptor && descriptor.value;
    } else targetProperty = target[key];
    FORCED = isForced(GLOBAL ? key : TARGET + (STATIC ? '.' : '#') + key, options.forced);
    // contained in target
    if (!FORCED && targetProperty !== undefined) {
      if (typeof sourceProperty == typeof targetProperty) continue;
      copyConstructorProperties(sourceProperty, targetProperty);
    }
    // add a flag to not completely full polyfills
    if (options.sham || (targetProperty && targetProperty.sham)) {
      createNonEnumerableProperty$1(sourceProperty, 'sham', true);
    }
    defineBuiltIn$1(target, key, sourceProperty, options);
  }
};

var wellKnownSymbol$3 = wellKnownSymbol$5;

var TO_STRING_TAG$1 = wellKnownSymbol$3('toStringTag');
var test = {};

test[TO_STRING_TAG$1] = 'z';

var toStringTagSupport = String(test) === '[object z]';

var TO_STRING_TAG_SUPPORT = toStringTagSupport;
var isCallable$2 = isCallable$d;
var classofRaw$1 = classofRaw$2;
var wellKnownSymbol$2 = wellKnownSymbol$5;

var TO_STRING_TAG = wellKnownSymbol$2('toStringTag');
var $Object = Object;

// ES3 wrong here
var CORRECT_ARGUMENTS = classofRaw$1(function () { return arguments; }()) == 'Arguments';

// fallback for IE11 Script Access Denied error
var tryGet = function (it, key) {
  try {
    return it[key];
  } catch (error) { /* empty */ }
};

// getting tag from ES6+ `Object.prototype.toString`
var classof$2 = TO_STRING_TAG_SUPPORT ? classofRaw$1 : function (it) {
  var O, tag, result;
  return it === undefined ? 'Undefined' : it === null ? 'Null'
    // @@toStringTag case
    : typeof (tag = tryGet(O = $Object(it), TO_STRING_TAG)) == 'string' ? tag
    // builtinTag case
    : CORRECT_ARGUMENTS ? classofRaw$1(O)
    // ES3 arguments fallback
    : (result = classofRaw$1(O)) == 'Object' && isCallable$2(O.callee) ? 'Arguments' : result;
};

var classof$1 = classof$2;

var $String = String;

var toString$5 = function (argument) {
  if (classof$1(argument) === 'Symbol') throw TypeError('Cannot convert a Symbol value to a string');
  return $String(argument);
};

var anObject$4 = anObject$7;

// `RegExp.prototype.flags` getter implementation
// https://tc39.es/ecma262/#sec-get-regexp.prototype.flags
var regexpFlags$1 = function () {
  var that = anObject$4(this);
  var result = '';
  if (that.hasIndices) result += 'd';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.dotAll) result += 's';
  if (that.unicode) result += 'u';
  if (that.unicodeSets) result += 'v';
  if (that.sticky) result += 'y';
  return result;
};

var fails$5 = fails$e;
var global$4 = global$f;

// babel-minify and Closure Compiler transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError
var $RegExp$2 = global$4.RegExp;

var UNSUPPORTED_Y$1 = fails$5(function () {
  var re = $RegExp$2('a', 'y');
  re.lastIndex = 2;
  return re.exec('abcd') != null;
});

// UC Browser bug
// https://github.com/zloirock/core-js/issues/1008
var MISSED_STICKY = UNSUPPORTED_Y$1 || fails$5(function () {
  return !$RegExp$2('a', 'y').sticky;
});

var BROKEN_CARET = UNSUPPORTED_Y$1 || fails$5(function () {
  // https://bugzilla.mozilla.org/show_bug.cgi?id=773687
  var re = $RegExp$2('^r', 'gy');
  re.lastIndex = 2;
  return re.exec('str') != null;
});

var regexpStickyHelpers = {
  BROKEN_CARET: BROKEN_CARET,
  MISSED_STICKY: MISSED_STICKY,
  UNSUPPORTED_Y: UNSUPPORTED_Y$1
};

var objectDefineProperties = {};

var internalObjectKeys = objectKeysInternal;
var enumBugKeys$1 = enumBugKeys$3;

// `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys
// eslint-disable-next-line es/no-object-keys -- safe
var objectKeys$1 = Object.keys || function keys(O) {
  return internalObjectKeys(O, enumBugKeys$1);
};

var DESCRIPTORS = descriptors;
var V8_PROTOTYPE_DEFINE_BUG = v8PrototypeDefineBug;
var definePropertyModule = objectDefineProperty;
var anObject$3 = anObject$7;
var toIndexedObject = toIndexedObject$4;
var objectKeys = objectKeys$1;

// `Object.defineProperties` method
// https://tc39.es/ecma262/#sec-object.defineproperties
// eslint-disable-next-line es/no-object-defineproperties -- safe
objectDefineProperties.f = DESCRIPTORS && !V8_PROTOTYPE_DEFINE_BUG ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject$3(O);
  var props = toIndexedObject(Properties);
  var keys = objectKeys(Properties);
  var length = keys.length;
  var index = 0;
  var key;
  while (length > index) definePropertyModule.f(O, key = keys[index++], props[key]);
  return O;
};

var getBuiltIn = getBuiltIn$3;

var html$1 = getBuiltIn('document', 'documentElement');

/* global ActiveXObject -- old IE, WSH */

var anObject$2 = anObject$7;
var definePropertiesModule = objectDefineProperties;
var enumBugKeys = enumBugKeys$3;
var hiddenKeys = hiddenKeys$4;
var html = html$1;
var documentCreateElement = documentCreateElement$1;
var sharedKey = sharedKey$2;

var GT = '>';
var LT = '<';
var PROTOTYPE = 'prototype';
var SCRIPT = 'script';
var IE_PROTO = sharedKey('IE_PROTO');

var EmptyConstructor = function () { /* empty */ };

var scriptTag = function (content) {
  return LT + SCRIPT + GT + content + LT + '/' + SCRIPT + GT;
};

// Create object with fake `null` prototype: use ActiveX Object with cleared prototype
var NullProtoObjectViaActiveX = function (activeXDocument) {
  activeXDocument.write(scriptTag(''));
  activeXDocument.close();
  var temp = activeXDocument.parentWindow.Object;
  activeXDocument = null; // avoid memory leak
  return temp;
};

// Create object with fake `null` prototype: use iframe Object with cleared prototype
var NullProtoObjectViaIFrame = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = documentCreateElement('iframe');
  var JS = 'java' + SCRIPT + ':';
  var iframeDocument;
  iframe.style.display = 'none';
  html.appendChild(iframe);
  // https://github.com/zloirock/core-js/issues/475
  iframe.src = String(JS);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(scriptTag('document.F=Object'));
  iframeDocument.close();
  return iframeDocument.F;
};

// Check for document.domain and active x support
// No need to use active x approach when document.domain is not set
// see https://github.com/es-shims/es5-shim/issues/150
// variation of https://github.com/kitcambridge/es5-shim/commit/4f738ac066346
// avoid IE GC bug
var activeXDocument;
var NullProtoObject = function () {
  try {
    activeXDocument = new ActiveXObject('htmlfile');
  } catch (error) { /* ignore */ }
  NullProtoObject = typeof document != 'undefined'
    ? document.domain && activeXDocument
      ? NullProtoObjectViaActiveX(activeXDocument) // old IE
      : NullProtoObjectViaIFrame()
    : NullProtoObjectViaActiveX(activeXDocument); // WSH
  var length = enumBugKeys.length;
  while (length--) delete NullProtoObject[PROTOTYPE][enumBugKeys[length]];
  return NullProtoObject();
};

hiddenKeys[IE_PROTO] = true;

// `Object.create` method
// https://tc39.es/ecma262/#sec-object.create
// eslint-disable-next-line es/no-object-create -- safe
var objectCreate = Object.create || function create(O, Properties) {
  var result;
  if (O !== null) {
    EmptyConstructor[PROTOTYPE] = anObject$2(O);
    result = new EmptyConstructor();
    EmptyConstructor[PROTOTYPE] = null;
    // add "__proto__" for Object.getPrototypeOf polyfill
    result[IE_PROTO] = O;
  } else result = NullProtoObject();
  return Properties === undefined ? result : definePropertiesModule.f(result, Properties);
};

var fails$4 = fails$e;
var global$3 = global$f;

// babel-minify and Closure Compiler transpiles RegExp('.', 's') -> /./s and it causes SyntaxError
var $RegExp$1 = global$3.RegExp;

var regexpUnsupportedDotAll = fails$4(function () {
  var re = $RegExp$1('.', 's');
  return !(re.dotAll && re.exec('\n') && re.flags === 's');
});

var fails$3 = fails$e;
var global$2 = global$f;

// babel-minify and Closure Compiler transpiles RegExp('(?<a>b)', 'g') -> /(?<a>b)/g and it causes SyntaxError
var $RegExp = global$2.RegExp;

var regexpUnsupportedNcg = fails$3(function () {
  var re = $RegExp('(?<a>b)', 'g');
  return re.exec('b').groups.a !== 'b' ||
    'b'.replace(re, '$<a>c') !== 'bc';
});

/* eslint-disable regexp/no-empty-capturing-group, regexp/no-empty-group, regexp/no-lazy-ends -- testing */
/* eslint-disable regexp/no-useless-quantifier -- testing */
var call$3 = functionCall;
var uncurryThis$7 = functionUncurryThis;
var toString$4 = toString$5;
var regexpFlags = regexpFlags$1;
var stickyHelpers = regexpStickyHelpers;
var shared = shared$4.exports;
var create = objectCreate;
var getInternalState = internalState.get;
var UNSUPPORTED_DOT_ALL = regexpUnsupportedDotAll;
var UNSUPPORTED_NCG = regexpUnsupportedNcg;

var nativeReplace = shared('native-string-replace', String.prototype.replace);
var nativeExec = RegExp.prototype.exec;
var patchedExec = nativeExec;
var charAt$3 = uncurryThis$7(''.charAt);
var indexOf = uncurryThis$7(''.indexOf);
var replace$2 = uncurryThis$7(''.replace);
var stringSlice$3 = uncurryThis$7(''.slice);

var UPDATES_LAST_INDEX_WRONG = (function () {
  var re1 = /a/;
  var re2 = /b*/g;
  call$3(nativeExec, re1, 'a');
  call$3(nativeExec, re2, 'a');
  return re1.lastIndex !== 0 || re2.lastIndex !== 0;
})();

var UNSUPPORTED_Y = stickyHelpers.BROKEN_CARET;

// nonparticipating capturing group, copied from es5-shim's String#split patch.
var NPCG_INCLUDED = /()??/.exec('')[1] !== undefined;

var PATCH = UPDATES_LAST_INDEX_WRONG || NPCG_INCLUDED || UNSUPPORTED_Y || UNSUPPORTED_DOT_ALL || UNSUPPORTED_NCG;

if (PATCH) {
  patchedExec = function exec(string) {
    var re = this;
    var state = getInternalState(re);
    var str = toString$4(string);
    var raw = state.raw;
    var result, reCopy, lastIndex, match, i, object, group;

    if (raw) {
      raw.lastIndex = re.lastIndex;
      result = call$3(patchedExec, raw, str);
      re.lastIndex = raw.lastIndex;
      return result;
    }

    var groups = state.groups;
    var sticky = UNSUPPORTED_Y && re.sticky;
    var flags = call$3(regexpFlags, re);
    var source = re.source;
    var charsAdded = 0;
    var strCopy = str;

    if (sticky) {
      flags = replace$2(flags, 'y', '');
      if (indexOf(flags, 'g') === -1) {
        flags += 'g';
      }

      strCopy = stringSlice$3(str, re.lastIndex);
      // Support anchored sticky behavior.
      if (re.lastIndex > 0 && (!re.multiline || re.multiline && charAt$3(str, re.lastIndex - 1) !== '\n')) {
        source = '(?: ' + source + ')';
        strCopy = ' ' + strCopy;
        charsAdded++;
      }
      // ^(? + rx + ) is needed, in combination with some str slicing, to
      // simulate the 'y' flag.
      reCopy = new RegExp('^(?:' + source + ')', flags);
    }

    if (NPCG_INCLUDED) {
      reCopy = new RegExp('^' + source + '$(?!\\s)', flags);
    }
    if (UPDATES_LAST_INDEX_WRONG) lastIndex = re.lastIndex;

    match = call$3(nativeExec, sticky ? reCopy : re, strCopy);

    if (sticky) {
      if (match) {
        match.input = stringSlice$3(match.input, charsAdded);
        match[0] = stringSlice$3(match[0], charsAdded);
        match.index = re.lastIndex;
        re.lastIndex += match[0].length;
      } else re.lastIndex = 0;
    } else if (UPDATES_LAST_INDEX_WRONG && match) {
      re.lastIndex = re.global ? match.index + match[0].length : lastIndex;
    }
    if (NPCG_INCLUDED && match && match.length > 1) {
      // Fix browsers whose `exec` methods don't consistently return `undefined`
      // for NPCG, like IE8. NOTE: This doesn't work for /(.?)?/
      call$3(nativeReplace, match[0], reCopy, function () {
        for (i = 1; i < arguments.length - 2; i++) {
          if (arguments[i] === undefined) match[i] = undefined;
        }
      });
    }

    if (match && groups) {
      match.groups = object = create(null);
      for (i = 0; i < groups.length; i++) {
        group = groups[i];
        object[group[0]] = match[group[1]];
      }
    }

    return match;
  };
}

var regexpExec$2 = patchedExec;

var $$1 = _export;
var exec$1 = regexpExec$2;

// `RegExp.prototype.exec` method
// https://tc39.es/ecma262/#sec-regexp.prototype.exec
$$1({ target: 'RegExp', proto: true, forced: /./.exec !== exec$1 }, {
  exec: exec$1
});

const isMobileDevice = /iPad|iPhone|iPod|tablet|Android|Mobile/.test(window.navigator.userAgent);

var NATIVE_BIND = functionBindNative;

var FunctionPrototype = Function.prototype;
var apply$1 = FunctionPrototype.apply;
var call$2 = FunctionPrototype.call;

// eslint-disable-next-line es/no-reflect -- safe
var functionApply = typeof Reflect == 'object' && Reflect.apply || (NATIVE_BIND ? call$2.bind(apply$1) : function () {
  return call$2.apply(apply$1, arguments);
});

var classofRaw = classofRaw$2;
var uncurryThis$6 = functionUncurryThis;

var functionUncurryThisClause = function (fn) {
  // Nashorn bug:
  //   https://github.com/zloirock/core-js/issues/1128
  //   https://github.com/zloirock/core-js/issues/1130
  if (classofRaw(fn) === 'Function') return uncurryThis$6(fn);
};

// TODO: Remove from `core-js@4` since it's moved to entry points

var uncurryThis$5 = functionUncurryThisClause;
var defineBuiltIn = defineBuiltIn$2;
var regexpExec$1 = regexpExec$2;
var fails$2 = fails$e;
var wellKnownSymbol$1 = wellKnownSymbol$5;
var createNonEnumerableProperty = createNonEnumerableProperty$3;

var SPECIES = wellKnownSymbol$1('species');
var RegExpPrototype = RegExp.prototype;

var fixRegexpWellKnownSymbolLogic = function (KEY, exec, FORCED, SHAM) {
  var SYMBOL = wellKnownSymbol$1(KEY);

  var DELEGATES_TO_SYMBOL = !fails$2(function () {
    // String methods call symbol-named RegEp methods
    var O = {};
    O[SYMBOL] = function () { return 7; };
    return ''[KEY](O) != 7;
  });

  var DELEGATES_TO_EXEC = DELEGATES_TO_SYMBOL && !fails$2(function () {
    // Symbol-named RegExp methods call .exec
    var execCalled = false;
    var re = /a/;

    if (KEY === 'split') {
      // We can't use real regex here since it causes deoptimization
      // and serious performance degradation in V8
      // https://github.com/zloirock/core-js/issues/306
      re = {};
      // RegExp[@@split] doesn't call the regex's exec method, but first creates
      // a new one. We need to return the patched regex when creating the new one.
      re.constructor = {};
      re.constructor[SPECIES] = function () { return re; };
      re.flags = '';
      re[SYMBOL] = /./[SYMBOL];
    }

    re.exec = function () { execCalled = true; return null; };

    re[SYMBOL]('');
    return !execCalled;
  });

  if (
    !DELEGATES_TO_SYMBOL ||
    !DELEGATES_TO_EXEC ||
    FORCED
  ) {
    var uncurriedNativeRegExpMethod = uncurryThis$5(/./[SYMBOL]);
    var methods = exec(SYMBOL, ''[KEY], function (nativeMethod, regexp, str, arg2, forceStringMethod) {
      var uncurriedNativeMethod = uncurryThis$5(nativeMethod);
      var $exec = regexp.exec;
      if ($exec === regexpExec$1 || $exec === RegExpPrototype.exec) {
        if (DELEGATES_TO_SYMBOL && !forceStringMethod) {
          // The native String method already delegates to @@method (this
          // polyfilled function), leasing to infinite recursion.
          // We avoid it by directly calling the native @@method method.
          return { done: true, value: uncurriedNativeRegExpMethod(regexp, str, arg2) };
        }
        return { done: true, value: uncurriedNativeMethod(str, regexp, arg2) };
      }
      return { done: false };
    });

    defineBuiltIn(String.prototype, KEY, methods[0]);
    defineBuiltIn(RegExpPrototype, SYMBOL, methods[1]);
  }

  if (SHAM) createNonEnumerableProperty(RegExpPrototype[SYMBOL], 'sham', true);
};

var uncurryThis$4 = functionUncurryThis;
var toIntegerOrInfinity$1 = toIntegerOrInfinity$4;
var toString$3 = toString$5;
var requireObjectCoercible$2 = requireObjectCoercible$5;

var charAt$2 = uncurryThis$4(''.charAt);
var charCodeAt = uncurryThis$4(''.charCodeAt);
var stringSlice$2 = uncurryThis$4(''.slice);

var createMethod$1 = function (CONVERT_TO_STRING) {
  return function ($this, pos) {
    var S = toString$3(requireObjectCoercible$2($this));
    var position = toIntegerOrInfinity$1(pos);
    var size = S.length;
    var first, second;
    if (position < 0 || position >= size) return CONVERT_TO_STRING ? '' : undefined;
    first = charCodeAt(S, position);
    return first < 0xD800 || first > 0xDBFF || position + 1 === size
      || (second = charCodeAt(S, position + 1)) < 0xDC00 || second > 0xDFFF
        ? CONVERT_TO_STRING
          ? charAt$2(S, position)
          : first
        : CONVERT_TO_STRING
          ? stringSlice$2(S, position, position + 2)
          : (first - 0xD800 << 10) + (second - 0xDC00) + 0x10000;
  };
};

var stringMultibyte = {
  // `String.prototype.codePointAt` method
  // https://tc39.es/ecma262/#sec-string.prototype.codepointat
  codeAt: createMethod$1(false),
  // `String.prototype.at` method
  // https://github.com/mathiasbynens/String.prototype.at
  charAt: createMethod$1(true)
};

var charAt$1 = stringMultibyte.charAt;

// `AdvanceStringIndex` abstract operation
// https://tc39.es/ecma262/#sec-advancestringindex
var advanceStringIndex$1 = function (S, index, unicode) {
  return index + (unicode ? charAt$1(S, index).length : 1);
};

var uncurryThis$3 = functionUncurryThis;
var toObject = toObject$2;

var floor = Math.floor;
var charAt = uncurryThis$3(''.charAt);
var replace$1 = uncurryThis$3(''.replace);
var stringSlice$1 = uncurryThis$3(''.slice);
// eslint-disable-next-line redos/no-vulnerable -- safe
var SUBSTITUTION_SYMBOLS = /\$([$&'`]|\d{1,2}|<[^>]*>)/g;
var SUBSTITUTION_SYMBOLS_NO_NAMED = /\$([$&'`]|\d{1,2})/g;

// `GetSubstitution` abstract operation
// https://tc39.es/ecma262/#sec-getsubstitution
var getSubstitution$1 = function (matched, str, position, captures, namedCaptures, replacement) {
  var tailPos = position + matched.length;
  var m = captures.length;
  var symbols = SUBSTITUTION_SYMBOLS_NO_NAMED;
  if (namedCaptures !== undefined) {
    namedCaptures = toObject(namedCaptures);
    symbols = SUBSTITUTION_SYMBOLS;
  }
  return replace$1(replacement, symbols, function (match, ch) {
    var capture;
    switch (charAt(ch, 0)) {
      case '$': return '$';
      case '&': return matched;
      case '`': return stringSlice$1(str, 0, position);
      case "'": return stringSlice$1(str, tailPos);
      case '<':
        capture = namedCaptures[stringSlice$1(ch, 1, -1)];
        break;
      default: // \d\d?
        var n = +ch;
        if (n === 0) return match;
        if (n > m) {
          var f = floor(n / 10);
          if (f === 0) return match;
          if (f <= m) return captures[f - 1] === undefined ? charAt(ch, 1) : captures[f - 1] + charAt(ch, 1);
          return match;
        }
        capture = captures[n - 1];
    }
    return capture === undefined ? '' : capture;
  });
};

var call$1 = functionCall;
var anObject$1 = anObject$7;
var isCallable$1 = isCallable$d;
var classof = classofRaw$2;
var regexpExec = regexpExec$2;

var $TypeError = TypeError;

// `RegExpExec` abstract operation
// https://tc39.es/ecma262/#sec-regexpexec
var regexpExecAbstract = function (R, S) {
  var exec = R.exec;
  if (isCallable$1(exec)) {
    var result = call$1(exec, R, S);
    if (result !== null) anObject$1(result);
    return result;
  }
  if (classof(R) === 'RegExp') return call$1(regexpExec, R, S);
  throw $TypeError('RegExp#exec called on incompatible receiver');
};

var apply = functionApply;
var call = functionCall;
var uncurryThis$2 = functionUncurryThis;
var fixRegExpWellKnownSymbolLogic = fixRegexpWellKnownSymbolLogic;
var fails$1 = fails$e;
var anObject = anObject$7;
var isCallable = isCallable$d;
var isNullOrUndefined = isNullOrUndefined$3;
var toIntegerOrInfinity = toIntegerOrInfinity$4;
var toLength = toLength$2;
var toString$2 = toString$5;
var requireObjectCoercible$1 = requireObjectCoercible$5;
var advanceStringIndex = advanceStringIndex$1;
var getMethod = getMethod$2;
var getSubstitution = getSubstitution$1;
var regExpExec = regexpExecAbstract;
var wellKnownSymbol = wellKnownSymbol$5;

var REPLACE = wellKnownSymbol('replace');
var max = Math.max;
var min = Math.min;
var concat = uncurryThis$2([].concat);
var push = uncurryThis$2([].push);
var stringIndexOf = uncurryThis$2(''.indexOf);
var stringSlice = uncurryThis$2(''.slice);

var maybeToString = function (it) {
  return it === undefined ? it : String(it);
};

// IE <= 11 replaces $0 with the whole match, as if it was $&
// https://stackoverflow.com/questions/6024666/getting-ie-to-replace-a-regex-with-the-literal-string-0
var REPLACE_KEEPS_$0 = (function () {
  // eslint-disable-next-line regexp/prefer-escape-replacement-dollar-char -- required for testing
  return 'a'.replace(/./, '$0') === '$0';
})();

// Safari <= 13.0.3(?) substitutes nth capture where n>m with an empty string
var REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE = (function () {
  if (/./[REPLACE]) {
    return /./[REPLACE]('a', '$0') === '';
  }
  return false;
})();

var REPLACE_SUPPORTS_NAMED_GROUPS = !fails$1(function () {
  var re = /./;
  re.exec = function () {
    var result = [];
    result.groups = { a: '7' };
    return result;
  };
  // eslint-disable-next-line regexp/no-useless-dollar-replacements -- false positive
  return ''.replace(re, '$<a>') !== '7';
});

// @@replace logic
fixRegExpWellKnownSymbolLogic('replace', function (_, nativeReplace, maybeCallNative) {
  var UNSAFE_SUBSTITUTE = REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE ? '$' : '$0';

  return [
    // `String.prototype.replace` method
    // https://tc39.es/ecma262/#sec-string.prototype.replace
    function replace(searchValue, replaceValue) {
      var O = requireObjectCoercible$1(this);
      var replacer = isNullOrUndefined(searchValue) ? undefined : getMethod(searchValue, REPLACE);
      return replacer
        ? call(replacer, searchValue, O, replaceValue)
        : call(nativeReplace, toString$2(O), searchValue, replaceValue);
    },
    // `RegExp.prototype[@@replace]` method
    // https://tc39.es/ecma262/#sec-regexp.prototype-@@replace
    function (string, replaceValue) {
      var rx = anObject(this);
      var S = toString$2(string);

      if (
        typeof replaceValue == 'string' &&
        stringIndexOf(replaceValue, UNSAFE_SUBSTITUTE) === -1 &&
        stringIndexOf(replaceValue, '$<') === -1
      ) {
        var res = maybeCallNative(nativeReplace, rx, S, replaceValue);
        if (res.done) return res.value;
      }

      var functionalReplace = isCallable(replaceValue);
      if (!functionalReplace) replaceValue = toString$2(replaceValue);

      var global = rx.global;
      if (global) {
        var fullUnicode = rx.unicode;
        rx.lastIndex = 0;
      }
      var results = [];
      while (true) {
        var result = regExpExec(rx, S);
        if (result === null) break;

        push(results, result);
        if (!global) break;

        var matchStr = toString$2(result[0]);
        if (matchStr === '') rx.lastIndex = advanceStringIndex(S, toLength(rx.lastIndex), fullUnicode);
      }

      var accumulatedResult = '';
      var nextSourcePosition = 0;
      for (var i = 0; i < results.length; i++) {
        result = results[i];

        var matched = toString$2(result[0]);
        var position = max(min(toIntegerOrInfinity(result.index), S.length), 0);
        var captures = [];
        // NOTE: This is equivalent to
        //   captures = result.slice(1).map(maybeToString)
        // but for some reason `nativeSlice.call(result, 1, result.length)` (called in
        // the slice polyfill when slicing native arrays) "doesn't work" in safari 9 and
        // causes a crash (https://pastebin.com/N21QzeQA) when trying to debug it.
        for (var j = 1; j < result.length; j++) push(captures, maybeToString(result[j]));
        var namedCaptures = result.groups;
        if (functionalReplace) {
          var replacerArgs = concat([matched], captures, position, S);
          if (namedCaptures !== undefined) push(replacerArgs, namedCaptures);
          var replacement = toString$2(apply(replaceValue, undefined, replacerArgs));
        } else {
          replacement = getSubstitution(matched, S, position, captures, namedCaptures, replaceValue);
        }
        if (position >= nextSourcePosition) {
          accumulatedResult += stringSlice(S, nextSourcePosition, position) + replacement;
          nextSourcePosition = position + matched.length;
        }
      }
      return accumulatedResult + stringSlice(S, nextSourcePosition);
    }
  ];
}, !REPLACE_SUPPORTS_NAMED_GROUPS || !REPLACE_KEEPS_$0 || REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE);

// a string of all valid unicode whitespaces
var whitespaces$2 = '\u0009\u000A\u000B\u000C\u000D\u0020\u00A0\u1680\u2000\u2001\u2002' +
  '\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200A\u202F\u205F\u3000\u2028\u2029\uFEFF';

var uncurryThis$1 = functionUncurryThis;
var requireObjectCoercible = requireObjectCoercible$5;
var toString$1 = toString$5;
var whitespaces$1 = whitespaces$2;

var replace = uncurryThis$1(''.replace);
var ltrim = RegExp('^[' + whitespaces$1 + ']+');
var rtrim = RegExp('(^|[^' + whitespaces$1 + '])[' + whitespaces$1 + ']+$');

// `String.prototype.{ trim, trimStart, trimEnd, trimLeft, trimRight }` methods implementation
var createMethod = function (TYPE) {
  return function ($this) {
    var string = toString$1(requireObjectCoercible($this));
    if (TYPE & 1) string = replace(string, ltrim, '');
    if (TYPE & 2) string = replace(string, rtrim, '$1');
    return string;
  };
};

var stringTrim = {
  // `String.prototype.{ trimLeft, trimStart }` methods
  // https://tc39.es/ecma262/#sec-string.prototype.trimstart
  start: createMethod(1),
  // `String.prototype.{ trimRight, trimEnd }` methods
  // https://tc39.es/ecma262/#sec-string.prototype.trimend
  end: createMethod(2),
  // `String.prototype.trim` method
  // https://tc39.es/ecma262/#sec-string.prototype.trim
  trim: createMethod(3)
};

var global$1 = global$f;
var fails = fails$e;
var uncurryThis = functionUncurryThis;
var toString = toString$5;
var trim = stringTrim.trim;
var whitespaces = whitespaces$2;

var $parseInt$1 = global$1.parseInt;
var Symbol$1 = global$1.Symbol;
var ITERATOR = Symbol$1 && Symbol$1.iterator;
var hex = /^[+-]?0x/i;
var exec = uncurryThis(hex.exec);
var FORCED = $parseInt$1(whitespaces + '08') !== 8 || $parseInt$1(whitespaces + '0x16') !== 22
  // MS Edge 18- broken with boxed symbols
  || (ITERATOR && !fails(function () { $parseInt$1(Object(ITERATOR)); }));

// `parseInt` method
// https://tc39.es/ecma262/#sec-parseint-string-radix
var numberParseInt = FORCED ? function parseInt(string, radix) {
  var S = trim(toString(string));
  return $parseInt$1(S, (radix >>> 0) || (exec(hex, S) ? 16 : 10));
} : $parseInt$1;

var $ = _export;
var $parseInt = numberParseInt;

// `parseInt` method
// https://tc39.es/ecma262/#sec-parseint-string-radix
$({ global: true, forced: parseInt != $parseInt }, {
  parseInt: $parseInt
});

/**
 * Translations handling utilities.
 *
 * These utilities provide functions to handle translations in DS components.
 * The consumer app **can** initialize custom translation functions, but it's optional.
 * If not initialized, the translation functions will return the key itself formatted with args if such supplied (or the singular or plural form based on the count in `args[0]` for plural translations.).
 */
// Translation functions that will be initialized by the consumer app
let translateFunction;
let translatePluralFunction;
/**
 * Initializes custom translation functions.
 *
 * The consumer app can provide a custom translation function to handle translations.
 * If the app doesn't initialize this, translation functions will fall back to using the key itself.
 *
 * @param translate - A function that handles translations. It takes a key and optional arguments.
 * @param translatePlural - (Optional) A function to handle plural translations.
 *
 * @example
 * initTranslator(
 *   (key) => translations[key] || key, // Your translation lookup logic
 *   (singular, plural, args) => args[0] > 1 ? plural : singular // Optional pluralization logic
 * );
 */
const initTranslator = (translate, translatePlural) => {
  translateFunction = translate;
  translatePluralFunction = translatePlural;
};
/**
 * Translates a key and replaces placeholders (e.g., `%1`, `%2`) with values from the `args` array.
 *
 * If no translator is initialized, the function will replace placeholders in the key and return it.
 *
 * @param key - The translation key (or the default string if no translator is provided).
 * @param args - (Optional) An array of values to replace placeholders like `%1`, `%2`.
 * @returns The translated or formatted string.
 *
 * @example
 * // Example with a placeholder:
 * translate('Hello %1', ['John']); // Output: 'Hello John' (if no translator, same output)
 *
 * // Example with multiple placeholders:
 * translate('Hello %1, you have %2 new messages', ['John', 5]); // Output: 'Hello John, you have 5 new messages'
 */
const translate = (key, args) => {
  if (!translateFunction) {
    return formatString(key, args);
  }
  return translateFunction(key, args);
};
/**
 * Handles plural translations.
 *
 * If no custom translator is initialized, it returns the singular or plural form based on the count in `args[0]`.
 *
 * @param singularString - The singular form.
 * @param pluralString - The plural form.
 * @param args - An array of arguments to determine which form to use, typically with a `count` at `args[0]`.
 * @returns The correct form based on the count or the custom translation function if initialized.
 *
 * @example
 * translatePlural('1 item', '%1 items', [1]); // Output: '1 item'
 * translatePlural('1 item', '%1 items', [5]); // Output: '5 items'
 */
const translatePlural = (singularString, pluralString, args) => {
  if (!translatePluralFunction) {
    const count = Number(args[0]);
    const plural = !Number.isNaN(count) && (count > 1 || count === 0);
    return plural ? translate(pluralString, args) : translate(singularString, args);
  }
  return translatePluralFunction(singularString, pluralString, args);
};
/**
 * Replaces placeholders (e.g., `%1`, `%2`) in the key with values from the `args` array.
 * This function is used when no translation function is provided to still format the string.
 *
 * @param key - The string with placeholders to be replaced (e.g., "Hello %1").
 * @param args - The array of values to replace the placeholders (e.g., ["John"]).
 * @returns The formatted string with the placeholders replaced.
 *
 * @example
 * formatString('Hello %1', ['John']); // Output: 'Hello John'
 */
const formatString = (key, args) => {
  if (!args || args.length === 0) {
    return key; // Return the original key if no arguments provided
  }
  // Replace %1, %2, etc., with corresponding values from args
  return key.replace(/%(\d+)/g, (match, index) => {
    const argIndex = parseInt(index, 10) - 1; // Convert index to zero-based
    return args[argIndex] !== undefined ? String(args[argIndex]) : match; // Replace if argument exists, else keep the placeholder
  });
};

export { BitsComponents, CustomizationsContext, CustomizationsProvider, InteractionsContext, InteractionsProvider, InteractionsTypes, MuiComponents, initTranslator, isMobileDevice, timezoneAbbr, translate, translatePlural, useGlobalClassesForComponentsProps, useMuiGlobalClassesForComponentsProps };
